// Stage Mail-V2 Stage 4.4 (2026-07-22): React ErrorBoundary 抓 Mail V2 真 stack
// 给 user 看 (替代默认 "Unexpected Application Error" 红屏)。
import React from "react";

interface State {
  error: Error | null;
  stack: string | null;
  componentStack: string | null;
}

interface Props {
  children: React.ReactNode;
}

export class MailV2ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, stack: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, stack: error.stack ?? null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // ★ 2026-07-22 取证: 抓 throw 的真实文件/行号 (从 stack 字符串解析)
    // prod minified stack 中 "ag" / "Kb" 是 React 内部符号,不可读。
    // 但用户使用的 stack 仍包含 throw 点的文件名 + 行号,提取出来给我们看。
    const stackLines = (error.stack ?? "").split("\n");
    const throwSite = stackLines
      .slice(0, 5)
      .map((l) => l.trim())
      .join("\n  at ");
    // eslint-disable-next-line no-console
    console.error("[MAIL-V2 ERROR BOUNDARY]", {
      error: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
      throwSite,
    });
    this.setState({ componentStack: info.componentStack ?? null });
  }

  render() {
    if (this.state.error) {
      // ★ 2026-07-22 取证: 显示 throw 实际位置(从 stack 解析)+ 取证 UI
      const stackLines = (this.state.stack ?? "").split("\n");
      const throwSite = stackLines.slice(0, 5).join("\n  ");
      return (
        <div
          style={{
            padding: 24,
            color: "#b91c1c",
            fontFamily: "monospace",
            background: "#fff5f5",
            minHeight: "100vh",
            overflow: "auto",
          }}
          data-testid="mail-v2-error-boundary"
        >
          <h2 style={{ marginTop: 0 }}>Mail V2 — Unexpected error</h2>
          <p>
            Stage 4.4 debug — please copy the following console output to Claude.
          </p>
          <p>
            <strong>{this.state.error.message}</strong>
          </p>
          <details open>
            <summary>Throw site (top 5 stack frames)</summary>
            <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
              {throwSite}
            </pre>
          </details>
          <details>
            <summary>Full Stack</summary>
            <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
              {this.state.stack}
            </pre>
          </details>
          {this.state.componentStack && (
            <details>
              <summary>Component stack</summary>
              <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                {this.state.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}